/**
 * cockpit.ts — the first-person COCKPIT for space + atmospheric flight
 * (2026-08-24; detail pass 2026-08-27).
 *
 * The camera moves into the ship's nose (cockpitEye) for the first-person
 * viewpoint; the canopy itself is a SCREEN-SPACE overlay drawn on a 2D canvas
 * over the 3D view — the same DOM layer as the HUD reticle/radar. (This
 * engine's ECS renderer only draws registered meshes, so an ad-hoc Object3D
 * canopy parented to the camera never rendered — the wave-1..3 miss; the
 * overlay is guaranteed visible and costs nothing.)
 *
 * Each chassis gets a distinct canopy from its ROLE: frame tint + weight, how
 * far the glass wraps, dashboard height, MFD screens, round gauges, side
 * consoles with throttle levers, rivet detail, a glass glare, and blinking
 * warning lights — so a nimble interceptor, a bulky cargo runner, a heavy
 * gunship, and a personal cruiser each feel different from the seat. The frame
 * banks slightly with the turn for feel.
 */
import type { ChassisSpec } from './fleet.js';

export interface CockpitStyle {
  tint: string;       // trim / glow color
  frame: string;      // strut fill
  accent: string;     // secondary glow — screens, warning pips
  strut: number;      // strut thickness scale
  wrap: number;       // how far the A-pillars sit from the edges (0..1, smaller = wider glass)
  dash: number;       // dashboard height fraction of the viewport
  consoles: 0 | 1 | 2;
  mullion: boolean;
  screens: number;    // MFD panels on the dash (0..3)
  gauges: number;     // round dials on the dash (0..3)
  rivets: boolean;    // rivet detail on the struts (heavy hulls)
  glass: string;      // canopy glass tint (rgba)
  name: string;
}

export function cockpitStyle(spec: ChassisSpec): CockpitStyle {
  switch (spec.role) {
    case 'interceptor':      return { tint: '#8fd0ff', frame: '#101724', accent: '#5ffbe0', strut: 0.8, wrap: 0.09, dash: 0.13, consoles: 0, mullion: false, screens: 1, gauges: 1, rivets: false, glass: 'rgba(90,180,255,0.05)', name: spec.name };
    case 'cargo runner':     return { tint: '#ffce8a', frame: '#221a10', accent: '#ffd257', strut: 1.4,  wrap: 0.18, dash: 0.24, consoles: 1, mullion: true,  screens: 2, gauges: 2, rivets: true,  glass: 'rgba(255,200,120,0.06)', name: spec.name };
    case 'heavy gunship':    return { tint: '#ff9a7a', frame: '#241210', accent: '#ff5a44', strut: 1.8,  wrap: 0.20, dash: 0.26, consoles: 2, mullion: true,  screens: 3, gauges: 3, rivets: true,  glass: 'rgba(255,120,90,0.07)', name: spec.name };
    case 'personal cruiser': return { tint: '#d8c48a', frame: '#1a1428', accent: '#c2acff', strut: 1.25, wrap: 0.12, dash: 0.27, consoles: 2, mullion: true,  screens: 3, gauges: 2, rivets: false, glass: 'rgba(200,170,255,0.05)', name: spec.name };
    default:                 return { tint: '#8fc3ef', frame: '#141c2a', accent: '#7fe6ff', strut: 1.05, wrap: 0.13, dash: 0.20, consoles: 1, mullion: true,  screens: 2, gauges: 1, rivets: true,  glass: 'rgba(120,190,255,0.05)', name: spec.name }; // strike fighter
  }
}

/** Camera eye offset (rig-local, ship noses −Z): forward + up, scaled by hull. */
export function cockpitEye(spec: ChassisSpec): { y: number; z: number } {
  const s = Math.max(6, spec.hullSize);
  return { y: 0.5 + s * 0.05, z: -(0.4 + s * 0.1) };
}

function rrect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  g.beginPath();
  g.moveTo(x + rr, y);
  g.arcTo(x + w, y, x + w, y + h, rr);
  g.arcTo(x + w, y + h, x, y + h, rr);
  g.arcTo(x, y + h, x, y, rr);
  g.arcTo(x, y, x + w, y, rr);
  g.closePath();
}

/**
 * Draw the canopy over the whole viewport (w×h CSS px). `bank` (rad) leans the
 * frame into the turn. Pure 2D — safe to call every frame. A subtle time-based
 * pulse animates the warning light + a readout bar.
 */
export function drawCockpit(g: CanvasRenderingContext2D, w: number, h: number, st: CockpitStyle, bank: number): void {
  const t = (typeof performance !== 'undefined' ? performance.now() : 0) / 1000;
  g.clearRect(0, 0, w, h);
  g.save();
  // Bank: rotate the whole canopy a touch around the screen center.
  g.translate(w / 2, h / 2);
  g.rotate(Math.max(-0.06, Math.min(0.06, bank)));
  g.translate(-w / 2, -h / 2);

  const pad = w * st.wrap;                 // A-pillar inset from the sides
  const sill = h * (1 - st.dash);          // top of the dashboard
  const top = h * 0.055;                   // top rail height
  const sw = Math.max(6, w * 0.012 * st.strut); // strut thickness

  // Canopy glass tint + a diagonal glare streak (reads as real glass).
  g.fillStyle = st.glass;
  g.fillRect(0, 0, w, sill);
  g.save();
  g.globalCompositeOperation = 'screen';
  const glare = g.createLinearGradient(w * 0.1, 0, w * 0.55, sill);
  glare.addColorStop(0, 'rgba(255,255,255,0)');
  glare.addColorStop(0.5, 'rgba(255,255,255,0.05)');
  glare.addColorStop(0.55, 'rgba(255,255,255,0.12)');
  glare.addColorStop(0.6, 'rgba(255,255,255,0.05)');
  glare.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = glare;
  g.fillRect(0, 0, w, sill);
  g.restore();

  // Corner shading: a soft vignette so the canopy edges read as a hood.
  const vg = g.createRadialGradient(w / 2, h * 0.46, h * 0.26, w / 2, h * 0.5, h * 0.9);
  vg.addColorStop(0, 'rgba(0,0,0,0)');
  vg.addColorStop(1, 'rgba(3,5,10,0.6)');
  g.fillStyle = vg;
  g.fillRect(0, 0, w, h);

  // ── Dashboard: a solid panel across the bottom with a curved top lip ──
  const dashGrad = g.createLinearGradient(0, sill - h * 0.05, 0, h);
  dashGrad.addColorStop(0, st.frame);
  dashGrad.addColorStop(1, '#05070c');
  g.fillStyle = dashGrad;
  g.beginPath();
  g.moveTo(0, h);
  g.lineTo(0, sill + h * 0.03);
  g.quadraticCurveTo(w / 2, sill - h * 0.055, w, sill + h * 0.03);
  g.lineTo(w, h);
  g.closePath();
  g.fill();
  // Glowing readout band along the dash lip.
  g.strokeStyle = st.tint;
  g.lineWidth = Math.max(2, sw * 0.4);
  g.beginPath();
  g.moveTo(w * 0.14, sill + h * 0.02);
  g.quadraticCurveTo(w / 2, sill - h * 0.04, w * 0.86, sill + h * 0.02);
  g.stroke();

  // ── MFD screens on the dash (dark glass panels with live-looking bars) ──
  if (st.screens > 0) {
    const sy = sill + h * 0.055;
    const sh = h * 0.11;
    const swid = w * 0.14;
    const gap = w * 0.03;
    const totalW = st.screens * swid + (st.screens - 1) * gap;
    let sx = w / 2 - totalW / 2;
    for (let i = 0; i < st.screens; i++) {
      g.fillStyle = 'rgba(6,12,18,0.92)';
      rrect(g, sx, sy, swid, sh, 5); g.fill();
      g.strokeStyle = st.accent; g.globalAlpha = 0.5; g.lineWidth = 1.5;
      rrect(g, sx, sy, swid, sh, 5); g.stroke(); g.globalAlpha = 1;
      // scrolling bars / waveform
      g.fillStyle = st.accent;
      for (let b = 0; b < 5; b++) {
        const bh = (0.3 + 0.7 * Math.abs(Math.sin(t * 1.5 + i * 1.3 + b))) * (sh * 0.55);
        g.globalAlpha = 0.55;
        g.fillRect(sx + swid * 0.1 + b * swid * 0.16, sy + sh - h * 0.02 - bh, swid * 0.1, bh);
      }
      g.globalAlpha = 1;
      // header ticks
      g.fillStyle = st.tint; g.globalAlpha = 0.6;
      g.fillRect(sx + swid * 0.1, sy + sh * 0.16, swid * 0.5, 2);
      g.fillRect(sx + swid * 0.1, sy + sh * 0.30, swid * 0.32, 2);
      g.globalAlpha = 1;
      sx += swid + gap;
    }
  }

  // ── Round gauges flanking the screens ──
  for (let i = 0; i < st.gauges; i++) {
    const gx = i % 2 === 0 ? w * (0.10 + i * 0.015) : w * (0.90 - Math.floor(i / 2) * 0.03);
    const gy = sill + h * 0.11;
    const gr = h * 0.045;
    g.strokeStyle = st.frame; g.lineWidth = 4;
    g.beginPath(); g.arc(gx, gy, gr, 0, Math.PI * 2); g.stroke();
    g.strokeStyle = st.tint; g.lineWidth = 2; g.globalAlpha = 0.8;
    g.beginPath(); g.arc(gx, gy, gr, Math.PI * 0.75, Math.PI * 0.75 + Math.PI * (0.6 + 0.35 * Math.sin(t + i)), false); g.stroke();
    g.globalAlpha = 1;
    // needle
    const na = Math.PI * 0.75 + Math.PI * (0.5 + 0.4 * Math.sin(t * 0.7 + i * 2));
    g.strokeStyle = st.accent; g.lineWidth = 2;
    g.beginPath(); g.moveTo(gx, gy); g.lineTo(gx + Math.cos(na) * gr * 0.8, gy + Math.sin(na) * gr * 0.8); g.stroke();
  }

  // ── A-pillars: from the dashboard corners up to the top rail, splayed out ──
  const pillar = (dir: 1 | -1): void => {
    const xBot = dir < 0 ? pad * 0.4 : w - pad * 0.4;
    const xTop = dir < 0 ? pad : w - pad;
    const grad = g.createLinearGradient(xBot, sill, xTop, top);
    grad.addColorStop(0, st.frame);
    grad.addColorStop(0.5, '#05070c');
    grad.addColorStop(1, st.frame);
    g.fillStyle = grad;
    g.beginPath();
    g.moveTo(xBot - dir * sw, sill);
    g.lineTo(xBot + dir * sw, sill);
    g.lineTo(xTop + dir * sw, top);
    g.lineTo(xTop - dir * sw, top);
    g.closePath();
    g.fill();
    // tint edge highlight
    g.strokeStyle = st.tint; g.globalAlpha = 0.35; g.lineWidth = 2;
    g.beginPath(); g.moveTo(xBot - dir * sw, sill); g.lineTo(xTop - dir * sw, top); g.stroke();
    g.globalAlpha = 1;
    // rivets up the strut
    if (st.rivets) {
      g.fillStyle = st.tint; g.globalAlpha = 0.5;
      for (let k = 0.12; k < 0.95; k += 0.14) {
        const rx = xBot + (xTop - xBot) * k;
        const ry = sill + (top - sill) * k;
        g.beginPath(); g.arc(rx, ry, Math.max(1.5, sw * 0.18), 0, Math.PI * 2); g.fill();
      }
      g.globalAlpha = 1;
    }
  };
  pillar(-1); pillar(1);

  // ── Top rail joining the pillars, tinted ──
  g.fillStyle = st.frame;
  g.fillRect(pad - sw, top - sw, w - pad * 2 + sw * 2, sw * 2);
  g.fillStyle = st.tint; g.globalAlpha = 0.85;
  g.fillRect(pad, top + sw, w - pad * 2, Math.max(2, sw * 0.35));
  g.globalAlpha = 1;

  // Center mullion.
  if (st.mullion) {
    g.fillStyle = st.frame;
    g.fillRect(w / 2 - sw * 0.6, top, sw * 1.2, sill - top);
  }

  // ── Side consoles rising from the dashboard corners, with throttle levers ──
  for (let i = 0; i < st.consoles; i++) {
    const dir = st.consoles === 1 ? 1 : (i === 0 ? -1 : 1);
    const cx = dir < 0 ? w * 0.12 : w * 0.88;
    g.fillStyle = st.frame;
    rrect(g, cx - w * 0.055, sill - h * 0.03, w * 0.11, h * 0.13, 6); g.fill();
    // throttle lever slot + knob
    g.fillStyle = '#05070c';
    g.fillRect(cx - w * 0.008, sill - h * 0.01, w * 0.016, h * 0.09);
    const lever = sill + h * 0.02 + (dir < 0 ? h * 0.03 : h * 0.055);
    g.fillStyle = st.accent;
    rrect(g, cx - w * 0.018, lever, w * 0.036, h * 0.02, 3); g.fill();
    // console readout pips
    g.fillStyle = st.tint; g.globalAlpha = 0.75;
    for (let p = 0; p < 3; p++) g.fillRect(cx - w * 0.035, sill - h * 0.005 + p * h * 0.02, w * 0.02, 3);
    g.globalAlpha = 1;
  }

  // ── Warning light: a blinking pip on the dash lip ──
  const blink = 0.5 + 0.5 * Math.sin(t * 4);
  g.fillStyle = st.accent; g.globalAlpha = 0.35 + 0.5 * blink;
  g.beginPath(); g.arc(w * 0.5, sill + h * 0.04, Math.max(3, sw * 0.35), 0, Math.PI * 2); g.fill();
  g.globalAlpha = 1;

  // Indicator pips across the dash lip.
  g.fillStyle = st.tint; g.globalAlpha = 0.8;
  for (const fx of [0.22, 0.34, 0.66, 0.78]) g.fillRect(w * fx - 3, sill + h * 0.05, 6, 6);
  g.globalAlpha = 1;

  // Name plate on the dashboard.
  g.fillStyle = st.tint;
  g.globalAlpha = 0.7;
  g.font = `600 ${Math.round(h * 0.02)}px ui-monospace, monospace`;
  g.textAlign = 'center';
  g.fillText(`${st.name.toUpperCase()} · COCKPIT`, w / 2, h - h * 0.028);
  g.globalAlpha = 1;
  g.textAlign = 'start';
  g.restore();
}

/**
 * CockpitOverlay — a self-contained screen-space canopy shared by space flight
 * (SpaceModule) and atmospheric ship flight (AtlasVehicleModule). Owns its
 * canvas; the caller drives it: setChassis on mount/swap, show()/hide() on the
 * view toggle, draw(bank) each frame. The caller moves the camera to
 * cockpitEye(spec) for the first-person viewpoint.
 */
export class CockpitOverlay {
  private canvas: HTMLCanvasElement | null = null;
  private style: CockpitStyle | null = null;
  private shown = false;
  private lastBankQ = NaN;

  setChassis(spec: ChassisSpec): void { this.style = cockpitStyle(spec); }
  eye(spec: ChassisSpec): { y: number; z: number } { return cockpitEye(spec); }
  get visible(): boolean { return this.shown; }

  show(): void {
    if (!this.canvas) {
      const c = document.createElement('canvas');
      c.id = 'ship-cockpit';
      c.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:33;display:none';
      document.body.appendChild(c);
      this.canvas = c;
    }
    this.canvas.style.display = 'block';
    this.shown = true;
  }

  hide(): void {
    if (this.canvas) this.canvas.style.display = 'none';
    this.shown = false;
    this.lastBankQ = NaN;
  }

  /** Redraw the canopy (DPR-aware; banks with the turn). Call each frame. */
  draw(bank: number): void {
    const c = this.canvas;
    if (!this.shown || !c || !this.style) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = window.innerWidth, h = window.innerHeight;
    const resized = c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr);
    if (resized) { c.width = Math.round(w * dpr); c.height = Math.round(h * dpr); }
    // A full-window Canvas2D raster (gradients + ~200 paths at DPR) every frame
    // for an image that only changes with the bank: redraw when the bank moved
    // ≥ 0.005 rad or the canvas resized (2026-09-12).
    const q = Math.round(bank * 200);
    if (!resized && q === this.lastBankQ) return;
    this.lastBankQ = q;
    const g = c.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawCockpit(g, w, h, this.style, bank);
  }

  dispose(): void { this.canvas?.remove(); this.canvas = null; this.style = null; this.shown = false; }
}
